import { useCallback, useState } from 'react';
import { AiFillDelete } from 'react-icons/ai';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import Link from 'next/link';

import { Table, Tbody, Thead } from '@/components/Table';
import { useActiveSessionGuard } from '@/components/useActiveSessionGuard';
import { useAccount } from '@/components/useAccount';
import { Header } from '@/components/Header';
import { ContentWrapper } from '@/components/ContentWrapper';
import { useApiKeys } from '@/components/useApiKeys';
import { Button } from '@/components/Button';
import { Input, InputLabel } from '@/components/Input';
import { OverlayModal } from '@/components/OverlayModal';
import { Alert } from '@/components/Alert';
import { authedFetch } from '@/components/authedFetch';
import { TextLinkRaw } from '@/components/TextLink';
import { SecretLabel } from '@/components/SecretLabel';
import { ErrorResponse } from '@/util';
import { TransparentButton } from '@/components/TransparentButton';
import { TransparentInput } from '@/components/TransparentInput';
import { Footer } from '@/components/Footer';
import { NoDataText } from '@/components/NoDataText';

export default function ApiKeys() {
  useActiveSessionGuard(true, '/sign-in');
  const [error, setError] = useState<string>();
  const [isCreating, setIsCreating] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const { account } = useAccount();
  const { apiKeys, apiKeysError, apiKeysMutate } = useApiKeys();

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const name = (e.currentTarget.elements as any).name.value.trim();
      if (name.length < 1) {
        setError('Invalid name.');
        return;
      }

      setError(undefined);
      setIsCreating(true);

      authedFetch(`${process.env.NEXT_PUBLIC_API_URL}/api_keys`, {
        method: 'post',
        body: JSON.stringify({ name }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then(r => {
          if (!r.ok) {
            r.json()
              .then((json: ErrorResponse) => {
                setError(Object.values(json)[0][0]);
              })
              .catch(() => {
                setError('Invalid request.');
              });
            return;
          }
          return r.json().then(newKey => {
            setError(undefined);
            closeModal();
            if (apiKeys) {
              apiKeysMutate([...apiKeys, newKey]);
            } else {
              apiKeysMutate([newKey]);
            }
          });
        })
        .catch(() => {
          setError('An unknown error occurred. Please try again later.');
        })
        .then(() => {
          setIsCreating(false);
        });
    },
    [closeModal, apiKeysMutate, apiKeys],
  );

  const onDelete = useCallback(
    (id: number) => {
      setError(undefined);

      if (confirm('Are you sure you want to delete this API key?')) {
        authedFetch(`${process.env.NEXT_PUBLIC_API_URL}/api_keys/${id}`, {
          method: 'delete',
        })
          .then(() => {
            if (apiKeys) {
              apiKeysMutate(apiKeys.filter(key => key.id !== id));
            }
          })
          .catch(() => {
            setError('An unknown error occurred. Please try again later.');
          });
      }
    },
    [apiKeysMutate, apiKeys],
  );

  if (!account) {
    return null;
  }

  const apiKeyUsagePercentage =
    apiKeys !== undefined ? apiKeys.length / account.api_key_quota_grant : 0;

  return (
    <>
      <OverlayModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title="New API Key"
        className="fixed w-96"
      >
        <form onSubmit={onSubmit} className="flex flex-col gap-y-4">
          {error && <Alert type="warning">{error}</Alert>}

          <TransparentInput
            required
            minLength={1}
            name="name"
            placeholder="API KEY NAME"
          />

          <TransparentButton disabled={isCreating}>Create</TransparentButton>
        </form>
      </OverlayModal>

      <Header>
        <div className="absolute w-full h-full">
          <div className="flex flex-col justify-between h-full w-full">
            <ContentWrapper className="flex flex-col gap-y-4 max-w-5xl mx-auto w-full">
              {!account.stripe_payment_method_id && (
                <Alert type="warning">
                  You must{' '}
                  <Link href="/subscription">add a payment method</Link> before
                  creating an API key.
                </Alert>
              )}
              {apiKeys !== undefined && apiKeyUsagePercentage >= 0.66 && (
                <Alert
                  type={apiKeyUsagePercentage >= 1 ? 'warning' : 'default'}
                >
                  You are using {apiKeys.length} / {account.api_key_quota_grant}{' '}
                  of your allotted API keys.
                </Alert>
              )}
              <div className="flex items-center justify-between">
                <h1 className="text-3xl lg:text-4xl font-bold uppercase tracking-wide">
                  API Keys
                </h1>
                <TransparentButton
                  compact
                  onClick={openModal}
                  disabled={
                    !account.stripe_payment_method_id ||
                    apiKeys === undefined ||
                    (apiKeys !== undefined &&
                      apiKeys.length === account.api_key_quota_grant)
                  }
                >
                  + New API Key
                </TransparentButton>
              </div>

              {apiKeysError ? (
                <NoDataText spacer>
                  There was an error fetching API keys.
                </NoDataText>
              ) : apiKeys ? (
                apiKeys.length === 0 ? (
                  <NoDataText spacer>No keys have been created yet.</NoDataText>
                ) : (
                  <Table>
                    <Thead.Thead>
                      <Thead.Tr>
                        <Thead.Th>name</Thead.Th>
                        <Thead.Th className="px-4 lg:px-0">key</Thead.Th>
                        <Thead.Th>created</Thead.Th>
                        <Thead.Th>actions</Thead.Th>
                      </Thead.Tr>
                    </Thead.Thead>
                    <Tbody.Tbody>
                      {apiKeys.map(key => (
                        <Tbody.Tr key={key.id}>
                          <Tbody.Td>
                            <div
                              className="max-w-[3rem] overflow-hidden whitespace-nowrap text-ellipsis lg:max-w-[12rem]"
                              title={key.name}
                            >
                              {key.name}
                            </div>
                          </Tbody.Td>
                          <Tbody.Td className="px-4 lg:px-0">
                            <SecretLabel>{key.key_string}</SecretLabel>
                          </Tbody.Td>
                          <Tbody.Td>
                            {formatDistanceToNow(new Date(key.created_at))} ago
                          </Tbody.Td>
                          <Tbody.Td>
                            <div className="flex justify-end">
                              <TransparentButton
                                onClick={() => onDelete(key.id)}
                                compact
                              >
                                <AiFillDelete className="inline" />
                              </TransparentButton>
                            </div>
                          </Tbody.Td>
                        </Tbody.Tr>
                      ))}
                    </Tbody.Tbody>
                  </Table>
                )
              ) : (
                <NoDataText spacer>Loading...</NoDataText>
              )}
            </ContentWrapper>

            <Footer />
          </div>
        </div>
      </Header>
    </>
  );
}
